// @ts-strict-ignore
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { DrillingHoleSize } from '@3d-scene/model3d/util/drillinghole/drilling-hole-size';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Connection } from '../../connection';
import { Line } from '@3d-scene/model/line';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '../../container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectModel } from '@project/model/project.model';
import { CreateDrillingHolePresenter } from '@3d-scene/model3d/util/drillinghole/presenter/create-drilling-hole.presenter';
import { Injectable, Input } from '@angular/core';
import { RangeOnLineConnectionTypeDef } from '@project/constants/range-on-line-connection-type-def';
import { DrillingHolePresetPresenter } from './presenter/drilling-hole-preset.presenter';
import { DrillingHoleUtils } from './drilling-hole-utils';
import { ArticleDataService } from '@article/service/article-data.service';
import { ConnectionInfoUtils } from '../connection-info-utils';
import { ProServeProjectImportProcessor } from '@project/components/import-export/processor/pro-serve-project-import.processor';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { DrillingHoleInfoFactory } from './drilling-hole-info.factory';
import { ArticleService } from '@article/service/article.service';

@Injectable({ providedIn: 'root' })
export class DrillingHoleHelper {

  @Input() drillingHoleList: any[];
  public articleManager: ArticleManager;
  private _defaultDrillingHoles = [];
  private _articleUsage: ArticleUsageVO;
  private _parentArticle: ArticleVO;
  private _acceptingArea: ConnectionAreaVO;
  private _baseLine: Line = null;
  private _drillingHoleArticles: any[];
  private _allDrillingHoleArticles: ArticleVO[];
  private _searchDrillingHoles: DrillingHoleInfo[];

  constructor(
    public presenter: CreateDrillingHolePresenter,
    public projectModel: ProjectModel,
    public drillingholePreset: DrillingHolePresetPresenter,
    private proServeProjectImportProcessor: ProServeProjectImportProcessor,
    private articleDataService: ArticleDataService,
    private containerService: ContainerService,
    private articleUsageService: ArticleUsageService,
    private drillingHoleInfoFactory: DrillingHoleInfoFactory,
    private articleService: ArticleService
  ) {}

  public get articleUsage(): ArticleUsageVO {
    return this._articleUsage;
  }
  public set articleUsage(value: ArticleUsageVO) {
    this._articleUsage = value;
  }

  public async addDrillingHoles(articleUsageVO: ArticleUsageVO, parentArticle: ArticleVO, articleManager: ArticleManager, searchDrillingHoles: DrillingHoleInfo[]):	Promise<void> {
    this._parentArticle = parentArticle;
    this.articleManager = articleManager;
    this._searchDrillingHoles = searchDrillingHoles;
    this._articleUsage = articleUsageVO;
    await this.updateCurrentArticleUsageData();
    await this.loadDrillingHoleArticles();
  }

  private async updateCurrentArticleUsageData(): Promise<void> {
    if (!this._articleUsage || !this.articleUsageService.isMountingRailArticleUsage(this._articleUsage)) {
      this.clear();
      return;
    }
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._articleUsage.id);
    const acceptingConnectionArea: ConnectionAreaVO = ConnectionInfoUtils.findRangeOnLineConnectionByType(
      articleContainer.article.metaElements,
      RangeOnLineConnectionTypeDef.TYPE_DRILLING_HOLE
    );
    if (acceptingConnectionArea) {
      const acceptingConnection: Connection = articleContainer.findConnectionByName(acceptingConnectionArea.name);
      this._baseLine = new Line(acceptingConnection.localRectangle.point1, acceptingConnection.localRectangle.point4);
      this._parentArticle = articleContainer.article;
      this._acceptingArea = acceptingConnectionArea;
      this._drillingHoleArticles = null;
      // display drilling holes that are already connected
      this.refreshDrillingHoleList(this._articleUsage);
    }	else {
      await this.proServeProjectImportProcessor.drillingHoleNOTLoaded();
    }
  }

  private refreshDrillingHoleList(articleUsage: ArticleUsageVO): void	{
    const drillingHoleObjects: DrillingHoleInfo[] = [];
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    for (const guid of childGUIDs) {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(childUsage)) {
        const childArticle: ArticleVO = this.articleUsageService.getArticleByArticleUsage(childUsage);
        if (childArticle) {
          const drillingHoleInfo = this.drillingHoleInfoFactory.createForRefresh(childUsage, childArticle);
          drillingHoleObjects.push(drillingHoleInfo);
        }
      }
    }
    DrillingHoleUtils.sortDrillingHolesByPosition(drillingHoleObjects);
    this._defaultDrillingHoles = DrillingHoleUtils.copyDrillingHoleArray(drillingHoleObjects);
    this.drillingHoleList = [drillingHoleObjects];
  }



  private clear(): void {
    this.drillingHoleList = null;
    this._parentArticle = null;
    this._acceptingArea = null;
    this._baseLine = null;
    this._drillingHoleArticles = null;
    this._defaultDrillingHoles = [];
  }

  private async creatingNewHoles(drillingInfos: DrillingHoleInfo[]): Promise<void> {
    const removeDefaultHoles: DrillingHoleInfo[] = this._defaultDrillingHoles.concat();
    const createNewHoles: any[] = drillingInfos;
    if (createNewHoles.length !== 0)	{
      await this.presenter.createDrillingHoles(createNewHoles, this._articleUsage, this._drillingHoleArticles, this.drillingholePreset.getDrillingHoleGUIDs(removeDefaultHoles), this._baseLine);
    } else {
      await this.proServeProjectImportProcessor.drillingHoleNOTLoaded();
    }
  }

  private async loadDrillingHoleArticles(): Promise<void> {
    if (!this._allDrillingHoleArticles)	{
      const articles = await this.articleDataService.getAllArticlesByType(ArticleTypeIds.DRILLING_HOLE);
      await this.drillingHoleArticlesLoaded(articles);
    }	else {
      await this.loadSuitableDrillingHoles();
    }
  }

  private async loadSuitableDrillingHoles(): Promise<void>	{
    if (!this._drillingHoleArticles) {
      const requiredArticles: ArticleVO[] = this.articleService.findRequiredArticles(
        this._parentArticle,
        this._acceptingArea,
        this._allDrillingHoleArticles
      );
      if (requiredArticles.length > 0) {
        this._drillingHoleArticles = requiredArticles;
        await this.matchDrillingHolesByDrillDimensionID();
      }	else {
        this._drillingHoleArticles = [];
      }
    }
  }

  private async matchDrillingHolesByDrillDimensionID(): Promise<void> {
    const newDrillingHoles: DrillingHoleInfo[] = [];
    for (const articleVO of this._allDrillingHoleArticles) {
      const drillDimensionID: number = DrillingHoleUtils.getDrillDimensionIDByArticle(articleVO);
      const shapeType: string = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(articleVO);
      const holeSize: DrillingHoleSize = DrillingHoleUtils.getDrillingHoleSizeByArticle(articleVO);
      for (const hole of this._searchDrillingHoles) {
        const drillingSize: DrillingHoleSize = hole.size;
        // const serachSizeOnLine: number = drillingSize.sizeOnLine;
        const searchSizeLabel: string = drillingSize.sizeLabel;
        const searchShapeType: string = hole.shapeType;
        if (drillDimensionID === hole.drillDimmensionID) {
          hole.size = holeSize;
          hole.shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(articleVO);
          newDrillingHoles.push(hole);
        } else if (searchShapeType !== 'circle' && searchSizeLabel === holeSize.sizeLabel && hole.shapeType === shapeType) {
          hole.size = holeSize;
          newDrillingHoles.push(hole);
        } else if (searchShapeType === 'circle' && drillingSize.sizeLabel === holeSize.sizeLabel) {
          hole.size = holeSize;
          newDrillingHoles.push(hole);
        }
      }
    }
    await this.creatingNewHoles(newDrillingHoles);
  }

  public async drillingHoleArticlesLoaded(articles: ArticleVO[]): Promise<void> {
    this._allDrillingHoleArticles = articles;
    await this.loadSuitableDrillingHoles();
  }
}
